import { createContext, useContext } from "react";
import { AssessmentType } from "types/graphql-schema";

import { ComplianceProgramType } from "./components/compliance-program-form/types";

/**
 * Describes the "section" that the shared compliance-program components (list,
 * detail, form, action dropdown) are being rendered inside of.
 *
 * Historically these components lived only under `/programs`. They are now also
 * embedded inside the Reviews (`/assessments`) and Surveys (`/survey`) apps, so
 * their links, breadcrumbs, list filter, and locked program type need to change
 * depending on where they are mounted. Rather than prop-drilling through many
 * layers (the action dropdown is buried inside both the list and the detail),
 * the mounting app provides this context and the shared components read from it.
 *
 * The default matches the legacy `/programs` behaviour.
 */
export type ProgramsSection = {
  /** Base path for program detail/form routes. Row/detail links are `${basePath}/${id}`. */
  basePath: string;
  /** Where the program list lives (e.g. the "All" tab). Used as the return target after create. */
  listUrl: string;
  /** First breadcrumb crumb for detail/form pages within this section. */
  rootCrumb: { title: string; url: string };
  /** When set, program creation is locked to this type and the type chooser is hidden. */
  lockedProgramType?: ComplianceProgramType;
  /** When set, the list only shows programs that include one of these assessment types. */
  assessmentTypes?: AssessmentType[];
  /** User-facing singular noun for items in this section, capitalized (e.g. "Review", "Survey"). */
  noun: string;
  /** User-facing plural noun for items in this section, capitalized (e.g. "Reviews", "Surveys"). */
  nounPlural: string;
  /** Placeholder shown when the list is empty. */
  listEmptyText: string;
};

const defaultSection: ProgramsSection = {
  basePath: "/programs",
  listUrl: "/programs",
  rootCrumb: { title: "Programs", url: "/programs" },
  noun: "Program",
  nounPlural: "Programs",
  listEmptyText: "No programs.",
};

export const ProgramsSectionContext =
  createContext<ProgramsSection>(defaultSection);

export const useProgramsSection = () => useContext(ProgramsSectionContext);
